import { useState, useEffect, useCallback, useContext } from 'react';
import { useNavigate } from 'react-router';
import { Box, Typography, Button, Stack, IconButton, Chip } from '@mui/joy';
import { motion } from 'framer-motion';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';

import Header from '../../components/Header';
import MemeLoader from '../../components/MemeLoader';
import { AppContext } from '../../context';
import {
    APIGetPendingOrderDeletionRequests, APIReviewOrderDeletionRequest, parseApiError,
} from '../../api';

const fmtMoney = (v) => Number(v || 0).toLocaleString('ru-RU', { maximumFractionDigits: 0 });

export default function OrderDeletionRequestsPage() {
    const { token, showToast, onRefreshTick } = useContext(AppContext);
    const navigate = useNavigate();
    const [reqs, setReqs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busyId, setBusyId] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        const res = await APIGetPendingOrderDeletionRequests(token);
        setReqs(Array.isArray(res) ? res : []);
        setLoading(false);
    }, [token]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => onRefreshTick(load), [onRefreshTick, load]);

    const review = async (req, action) => {
        let comment = '';
        if (action === 'reject') comment = window.prompt('Причина отклонения (необязательно):') || '';
        setBusyId(req.id);
        const res = await APIReviewOrderDeletionRequest(token, req.id, action, comment);
        setBusyId(null);
        if (res?.error) { showToast(parseApiError(res.error, 'Не удалось'), 'danger'); return; }
        showToast(action === 'approve' ? 'Чек удалён ✓' : 'Заявка отклонена',
            action === 'approve' ? 'success' : 'warning');
        setReqs(prev => prev.filter(r => r.id !== req.id));
    };

    return (
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
            style={{ minHeight: '100vh', background: 'var(--joy-palette-background-body)' }}>
            <Header />
            <Box sx={{ maxWidth: 720, mx: 'auto', px: { xs: 2, sm: 3 }, py: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                    <IconButton variant="plain" onClick={() => navigate('/manager')} sx={{ color: 'var(--joy-palette-text-secondary)' }}>
                        <ArrowBackRoundedIcon />
                    </IconButton>
                    <Typography sx={{ fontSize: 22, fontWeight: 800 }}>Заявки на удаление чеков</Typography>
                </Box>

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><MemeLoader /></Box>
                ) : reqs.length === 0 ? (
                    <Box sx={{
                        textAlign: 'center', py: 8, borderRadius: 'var(--joy-radius-xl)',
                        border: '1px dashed var(--joy-palette-divider)', bgcolor: 'var(--joy-palette-background-level1)',
                    }}>
                        <Typography sx={{ fontSize: 13, color: 'var(--joy-palette-text-tertiary)' }}>
                            Нет заявок на рассмотрении
                        </Typography>
                    </Box>
                ) : (
                    <Stack spacing={1.5}>
                        {reqs.map(req => (
                            <Box key={req.id} sx={{
                                bgcolor: 'var(--joy-palette-background-level1)',
                                border: '1px solid var(--joy-palette-divider)',
                                borderRadius: 'var(--joy-radius-lg)', p: 2,
                            }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mb: 1 }}>
                                    <Box sx={{ minWidth: 0 }}>
                                        <Typography sx={{ fontSize: 15, fontWeight: 800 }}>
                                            Чек на {fmtMoney(req.amount_snapshot)} ₽
                                        </Typography>
                                        <Typography sx={{ fontSize: 12, color: 'var(--joy-palette-text-secondary)' }}>
                                            {req.photographer_name_snapshot || '—'}
                                            {req.location_name ? ` · ${req.location_name}` : ''}
                                        </Typography>
                                    </Box>
                                    <Chip size="sm" variant="soft" color="warning" sx={{ fontSize: 10, fontWeight: 700, height: 22 }}>
                                        {req.requested_by_name || 'аккаунт'}
                                    </Chip>
                                </Box>
                                <Typography sx={{ fontSize: 13, color: 'var(--joy-palette-text-primary)', mb: 1.5 }}>
                                    Причина: {req.comment}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Button size="sm" variant="soft" color="neutral" disabled={busyId === req.id}
                                        onClick={() => review(req, 'reject')} sx={{ fontWeight: 700 }}>
                                        Отклонить
                                    </Button>
                                    <Button size="sm" color="danger" loading={busyId === req.id}
                                        onClick={() => review(req, 'approve')} sx={{ fontWeight: 800 }}>
                                        Одобрить и удалить
                                    </Button>
                                </Box>
                            </Box>
                        ))}
                    </Stack>
                )}
            </Box>
        </motion.div>
    );
}